import React, { useRef, useState, useEffect } from "react";

interface VideoSectionProps {
	videoId?: string;
	videoType?: "youtube" | "vimeo" | "self-hosted";
	videoSrc?: string;
	posterImage?: string;
	aspectRatio?: string;
	bgColor?: string;
	autoPlay?: boolean;
	muted?: boolean;
	loop?: boolean;
	controls?: boolean;
	lazyLoad?: boolean;
}

const VideoSection: React.FC<VideoSectionProps> = ({
	videoId,
	videoType = "youtube",
	videoSrc,
	posterImage,
	aspectRatio = "16/9",
	bgColor = "bg-gray-100",
	autoPlay = false,
	muted = true,
	loop = false,
	controls = true,
	lazyLoad = true,
}) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const [isVisible, setIsVisible] = useState(!lazyLoad);

	useEffect(() => {
		if (!lazyLoad) return;

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					setIsVisible(true);
					observer.disconnect();
				}
			},
			{ rootMargin: "200px" }
		);

		if (containerRef.current) {
			observer.observe(containerRef.current);
		}

		return () => observer.disconnect();
	}, [lazyLoad]);
	const getEmbedUrl = () => {
		switch (videoType) {
			case "youtube":
				return `https://www.youtube.com/embed/${videoId}?rel=0&modestbranding=1`;
			case "vimeo":
				return `https://player.vimeo.com/video/${videoId}?title=0&byline=0&portrait=0`;
			default:
				return "";
		}
	};

	const embedUrl = videoType !== "self-hosted" ? getEmbedUrl() : "";

	return (
		<div ref={containerRef} className={`${bgColor} py-12 md:py-20`}>
			<div className="container mx-auto px-4 max-w-5xl">
				<div
					className="relative w-full rounded-xl overflow-hidden shadow-2xl bg-gray-900"
					style={{ aspectRatio }}
				>
					{!isVisible ? (
						<div className="absolute inset-0 w-full h-full bg-gray-900 flex items-center justify-center">
							<div className="w-12 h-12 border-4 border-white/20 border-t-white rounded-full animate-spin" />
						</div>
					) : videoType === "self-hosted" && videoSrc ? (
						<video
							src={videoSrc}
							poster={posterImage}
							autoPlay={autoPlay}
							muted={muted}
							loop={loop}
							controls={controls}
							playsInline
							preload="metadata"
							className="absolute inset-0 w-full h-full object-cover"
						/>
					) : embedUrl ? (
						<iframe
							src={embedUrl}
							title="Video"
							allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
							allowFullScreen
							className="absolute inset-0 w-full h-full"
						/>
					) : posterImage ? (
						<div
							className="absolute inset-0 w-full h-full bg-cover bg-center flex items-center justify-center"
							style={{ backgroundImage: `url(${posterImage})` }}
						>
							<div className="w-20 h-20 bg-white/90 rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:bg-white transition-colors">
								<svg
									className="w-8 h-8 text-gray-800 ml-1"
									fill="currentColor"
									viewBox="0 0 24 24"
								>
									<path d="M8 5v14l11-7z" />
								</svg>
							</div>
						</div>
					) : (
						<div className="absolute inset-0 w-full h-full bg-gray-300 flex items-center justify-center">
							<p className="text-gray-500 font-display">Video Placeholder</p>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

export default VideoSection;
